import Vue from 'vue'
import { assert } from 'chai'
import { apolloClient } from '~services/apollo'
import _ from 'lodash'
import Store from '~store/store'
import Utils from '~utils/utils'

const promisesQueues = {}

export const createPromisesQueue = (queueName, concurrentPromisses, finishedEvent, progressEvent) => {
  promisesQueues[queueName] = []
  promisesQueues[queueName].runningPromises = 0
  promisesQueues[queueName].errors = 0
  promisesQueues[queueName].resultIds = []
  promisesQueues[queueName].concurrentPromisses = concurrentPromisses
  promisesQueues[queueName].finishedEvent = finishedEvent
  promisesQueues[queueName].progressEvent = progressEvent
}

export const enqueuePromise = (functionName, payload, queueName) => {
  if (!promisesQueues[queueName]) {
    throw new Error('First you need to create the queue.')
  }

  const promise = {
    functionName,
    payload
  }

  promisesQueues[queueName].push(promise)
  proccessPromiseQueue(queueName)
}

const proccessPromiseQueue = queueName => {
  if (!promisesQueues[queueName].length) {
    return
  }

  if (promisesQueues[queueName].runningPromises === promisesQueues[queueName].concurrentPromisses) return

  new Promise(resolve => {
    const nextFunction = promisesQueues[queueName].shift()
    promisesQueues[queueName].runningPromises += 1
    Store.dispatch(nextFunction.functionName, nextFunction.payload)
      .then(result => {
        if (result.id) {
          promisesQueues[queueName].resultIds.push(result.id)
        }
        resolve()
      })
      .catch(() => {
        if (!promisesQueues[queueName].errors) {
          promisesQueues[queueName].errors = 0
        }
        promisesQueues[queueName].errors += 1
        resolve()
      })
  }).then(() => {
    promisesQueues[queueName].runningPromises -= 1
    if (promisesQueues[queueName].progressEvent) {
      Vue.bus.$emit(promisesQueues[queueName].progressEvent, {
        remainingItems: promisesQueues[queueName].length,
        errors: promisesQueues[queueName].errors
      })
    }
    if (!promisesQueues[queueName].length && !promisesQueues[queueName].finished && promisesQueues[queueName].runningPromises === 0) {
      if (promisesQueues[queueName].finishedEvent) {
        Vue.bus.$emit(promisesQueues[queueName].finishedEvent, { ids: promisesQueues[queueName].resultIds, errors: promisesQueues[queueName].errors })
      }

      promisesQueues[queueName].runningPromises = 0
      promisesQueues[queueName].finished = true
    } else if (!promisesQueues[queueName].finished) {
      proccessPromiseQueue(queueName)
    }
  })
}

export default {
  getItems({ dispatch, commit }, payload) {
    assert.exists(payload.query)
    assert.exists(payload.resultPath)

    dispatch('startLoading', payload.loader)
    return new Promise((resolve, reject) => {
      apolloClient
        .query({
          query: payload.query,
          variables: payload.variables,
          fetchPolicy: !payload.useCache ? 'no-cache' : 'cache-first'
        })
        .then(response => {
          const data = _.get(response.data, payload.resultPath)
          if (payload.modelName) {
            payload.items = data
            commit('SET_ITEMS', payload)
          }

          const totalCount = _.get(response.data, payload.totalPath) || null
          if (payload.totalModelName) {
            payload.totalCount = totalCount
            commit('SET_COUNT', payload)
          }

          resolve(data, totalCount)
        })
        .catch(response => {
          reject(response)
        })
    }).finally(() => {
      dispatch('endLoading', payload.loader)
    })
  },
  getItemsCSV({ dispatch }, payload) {
    assert.exists(payload.query)
    assert.exists(payload.modelName)

    dispatch('startLoading', payload.loader)
    return new Promise((resolve, reject) => {
      apolloClient
        .query({
          query: payload.query,
          variables: payload.variables
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(response => {
          reject(response)
        })
    }).finally(() => {
      dispatch('endLoading', payload.loader)
    })
  },
  getItemById({ dispatch, commit }, payload) {
    assert.exists(payload.resultPath)
    assert.exists(payload.query)
    assert.exists(payload.id)

    dispatch('startLoading', payload.loader)
    return new Promise((resolve, reject) => {
      apolloClient
        .query({
          query: payload.query,
          variables: {
            id: payload.id
          },
          fetchPolicy: !payload.useCache ? 'no-cache' : 'cache-first'
        })
        .then(response => {
          const data = _.get(response.data, payload.resultPath)
          payload.item = data
          if (payload.modelName && response) {
            commit('SET_ITEM', payload)
          }

          if (payload.addTo) {
            commit('PERSIST_ITEM', payload)
          }

          resolve(response && data)
        })
        .catch(response => {
          reject(response)
        })
    }).finally(() => {
      dispatch('endLoading', payload.loader)
    })
  },
  createItems({ dispatch }, payload) {
    assert.exists(payload.resultPath)
    assert.exists(payload.mutation)
    assert.exists(payload.items)

    return new Promise(resolve => {
      if (payload.items.length === 0) {
        resolve()
        return
      }

      const bulkLoader = payload.loader
      const finishedEvent = payload.finishedEvent || bulkLoader + ':finished'
      const concurrentPromisses = 3
      const queueName = Utils.uuid()

      createPromisesQueue(queueName, concurrentPromisses, finishedEvent, payload.progressEvent)

      dispatch('startLoading', bulkLoader)

      payload.loader = payload.loader + ':item'

      payload.items.forEach(item => {
        const itemPayload = _.cloneDeep(_.omit(payload, ['state', 'items']))
        itemPayload.state = payload.state
        itemPayload.item = item

        enqueuePromise('createItem', itemPayload, queueName)
      })

      Vue.bus.$once(finishedEvent, () => {
        dispatch('endLoading', bulkLoader)
        resolve()
      })
    })
  },
  createItem({ dispatch, commit }, payload) {
    assert.exists(payload.resultPath)
    assert.exists(payload.mutation)
    assert.exists(payload.item)

    const payloadClone = _.cloneDeep(_.omit(payload, ['state']))
    payloadClone.state = payload.state
    payloadClone.removeFrom = payloadClone.addTo

    return new Promise((resolve, reject) => {
      dispatch('startLoading', payloadClone.loader)
      apolloClient
        .mutate({
          mutation: payload.mutation,
          variables: payloadClone.item
        })
        .then(response => {
          payloadClone.item = _.get(response.data, payload.resultPath)
          payloadClone.id = payloadClone.item.id
          if (payloadClone.modelName) {
            commit('SET_ITEM', payloadClone)
          }
          if (payloadClone.addTo) {
            commit('PERSIST_ITEM', payloadClone)
          }
          resolve(payloadClone.item)
        })
        .catch(response => {
          reject(response)
        })
    }).finally(() => {
      dispatch('endLoading', payloadClone.loader)
    })
  },
  deleteItem({ dispatch, commit, state }, payload) {
    assert.exists(payload.mutation)
    assert.exists(payload.id)

    const destinationState = payload.state || state

    if (payload.earlyRemove && payload.modelName && payload.id === destinationState[payload.modelName].id) {
      // TODO: implementar regra para voltar o item para o vuex em caso de falha no DELETE no lado do servidor
      payload.item = {}
      commit('SET_ITEM', payload)
      delete payload.item
    }

    if (payload.earlyRemove && payload.removeFrom) {
      // TODO: implementar regra para voltar o item para o vuex em caso de falha no DELETE no lado do servidor
      commit('DELETE_ITEM', payload)
    }

    dispatch('startLoading', payload.loader)
    return new Promise((resolve, reject) => {
      apolloClient
        .mutate({
          mutation: payload.mutation,
          variables: {
            id: payload.id
          }
        })
        .then(response => {
          if (!payload.earlyRemove && payload.modelName && payload.id === destinationState[payload.modelName].id) {
            payload.item = {}
            commit('SET_ITEM', payload)
            delete payload.item
          }

          if (!payload.earlyRemove && payload.removeFrom) {
            commit('DELETE_ITEM', payload)
          }

          resolve(response)
        })
        .catch(response => {
          reject(response)
        })
    }).finally(() => {
      dispatch('endLoading', payload.loader)
    })
  },
  updateItems({ dispatch }, payload) {
    assert.exists(payload.resultPath)
    assert.exists(payload.mutation)
    assert.exists(payload.items)

    return new Promise(resolve => {
      if (payload.items.length === 0) {
        resolve()
        return
      }

      const bulkLoader = payload.loader
      const finishedEvent = payload.finishedEvent || bulkLoader + ':finished'
      const concurrentPromisses = 3
      const queueName = Utils.uuid()

      createPromisesQueue(queueName, concurrentPromisses, finishedEvent, payload.progressEvent)

      dispatch('startLoading', bulkLoader)

      payload.loader = payload.loader + ':item'

      payload.items.forEach(item => {
        const itemPayload = _.cloneDeep(_.omit(payload, ['state', 'items']))
        itemPayload.state = payload.state
        itemPayload.item = item

        enqueuePromise('updateItem', itemPayload, queueName)
      })

      Vue.bus.$once(finishedEvent, () => {
        dispatch('endLoading', bulkLoader)
        resolve()
      })
    })
  },
  updateItem({ dispatch, commit, state }, payload) {
    assert.exists(payload.resultPath)
    assert.exists(payload.mutation)
    assert.exists(payload.item)

    dispatch('startLoading', payload.loader)
    return new Promise((resolve, reject) => {
      apolloClient
        .mutate({
          mutation: payload.mutation,
          variables: payload.item
        })
        .then(response => {
          const result = response ? _.get(response.data, payload.resultPath) : payload.item // workaround
          payload.id = result.id
          payload.item = result

          if (payload.modelName && (payload.state || state)[payload.modelName].id === payload.id) {
            commit('SET_ITEM', payload)
          }

          if (payload.addTo) {
            commit('PERSIST_ITEM', payload)
          }

          if (payload.removeFrom) {
            // for soft delete
            commit('DELETE_ITEM', payload)
          }

          resolve(result)
        })
        .catch(response => {
          reject(response)
        })
    }).finally(() => {
      dispatch('endLoading', payload.loader)
    })
  },
  setItem({ commit }, payload) {
    commit('SET_ITEM', payload)
  }
}
